/**
 * Расширения редактора, знающие про трекер: упоминания участников и ключи
 * задач. Всё, что не знает про API и роутер, живёт в дизайн-системе.
 */
import type { Extensions } from '@tiptap/core'
import { useRouter } from 'vue-router'

import { projectApi } from '@/api'
import { cdnUrl } from '@/api/cdn'

import { IssueKeyLink } from './issue-key-link'
import { createMentionExtension, type MentionUser } from './mention'

export { ISSUE_KEY_CLASS } from './issue-key-link'
export { uploadEditorImage } from './upload'
export type { MentionUser }

/** Ссылка в markdown, под которой едет упоминание: `[@Имя](user:UUID)` */
const MENTION_HREF = /\]\(user:([0-9a-f-]{36})\)/gi

/**
 * Идентификаторы упомянутых — из готового markdown, а не из живого редактора:
 * экран сохраняет строку, и собирать список из документа ему было бы неоткуда.
 * Бэкенду они нужны отдельным полем, markdown он не разбирает.
 */
export const collectMentionIds = (markdown: string): string[] => [
  ...new Set([...markdown.matchAll(MENTION_HREF)].map(([, id]) => id as string)),
]

const SUGGESTION_LIMIT = 8

/**
 * Кандидаты на упоминание — участники проекта. Всех пользователей системы
 * здесь показывать нельзя: упоминание подписывает человека на задачу, а
 * увидеть её может только участник проекта.
 */
const searchMembers = (projectId: string) => async (query: string): Promise<MentionUser[]> => {
  const { data } = await projectApi.projectServiceListProjectMembers(projectId)
  const needle = query.trim().toLowerCase()

  return (data.members ?? [])
    .map((member) => ({
      id: member.userId ?? '',
      name: member.displayName || member.userId || '',
      avatar: cdnUrl(member.avatarUrl),
    }))
    .filter((user) => user.id && (!needle || user.name.toLowerCase().includes(needle)))
    .slice(0, SUGGESTION_LIMIT)
}

/**
 * Набор расширений для экранов трекера. Вызывается в setup — берёт роутер,
 * чтобы переход по ключу задачи не перезагружал приложение.
 */
export const useTrackerEditorExtensions = (projectId: () => string): Extensions => {
  const router = useRouter()

  return [
    createMentionExtension((query) => searchMembers(projectId())(query)),
    IssueKeyLink.configure({
      href: (key) => `/issue/${key}`,
      open: (key) => {
        void router.push(`/issue/${key}`)
      },
    }),
  ]
}
